const DATE_COLUMN_KEYS = new Set(["created_at", "updated_at", "started_at", "completed_at"]);

export const AUTOMATION_OUTPUT_PROCESSING_STATUSES = [
  "pending",
  "running",
  "processing",
  "analysing_data",
  "analysing_invoice",
  "start",
];

export const isAutomationOutputProcessing = (status) =>
  AUTOMATION_OUTPUT_PROCESSING_STATUSES.includes(String(status ?? "").toLowerCase());

export const extractAutomationOutputsFromStreamPayload = (payload) => {
  if (!payload || typeof payload !== "object") return [];

  const outputs = [];
  const seen = new Set();

  const add = (item) => {
    if (!item || item.id == null || seen.has(item.id)) return;
    seen.add(item.id);
    outputs.push(item);
  };

  add(payload.automation_output);
  (payload.automation_outputs ?? []).forEach(add);
  (payload.manual_agent_outputs ?? []).forEach(add);

  return outputs;
};

export const extractAutomationOutputsFromStreamChunk = (chunk) => {
  if (!chunk) return [];
  return extractAutomationOutputsFromStreamPayload(chunk.payload ?? chunk);
};

const getValueAtPath = (obj, pathParts) => {
  let current = obj;
  for (const part of pathParts) {
    if (current == null || typeof current !== "object") return undefined;
    current = current[part];
  }
  return current;
};

export const getOutputColumnFieldName = (key) => {
  if (!key) return "";
  const parts = String(key).split(".");
  return parts[parts.length - 1] || String(key);
};

export const resolveOutputFieldValue = (output, key) => {
  if (!output || !key) return undefined;

  const keyStr = String(key);

  if (!keyStr.includes(".")) {
    if (Object.prototype.hasOwnProperty.call(output, keyStr)) {
      return output[keyStr];
    }
  }

  const pathParts = keyStr.split(".");

  if (pathParts[0] === "response" && output.fouray_response != null) {
    const nestedValue = getValueAtPath(output.fouray_response, pathParts);
    if (nestedValue !== undefined) return nestedValue;
  }

  if (output.fouray_response?.response != null) {
    const responseValue = getValueAtPath(output.fouray_response.response, pathParts);
    if (responseValue !== undefined) return responseValue;
  }

  const rootValue = getValueAtPath(output, pathParts);
  if (rootValue !== undefined) return rootValue;

  return undefined;
};

export const humanizeOutputColumnKey = (key) => {
  const fieldName = getOutputColumnFieldName(key);
  if (!fieldName) return "";
  return String(fieldName)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/** Uppercase the first character when it is lowercase; leave other values unchanged. */
export const capitalizeFirstLetter = (value) => {
  if (value == null || value === "") return value;

  const str = String(value);
  if (str === "—" || str === "N/A") return str;

  const first = str.charAt(0);
  if (first >= "a" && first <= "z") {
    return first.toUpperCase() + str.slice(1);
  }

  return str;
};

export const formatOutputCellValue = (output, key, formatDate) => {
  if (!output || !key) return "—";

  const raw = resolveOutputFieldValue(output, key);
  if (raw == null || raw === "") return "—";

  const fieldName = getOutputColumnFieldName(key);
  if (DATE_COLUMN_KEYS.has(fieldName) || DATE_COLUMN_KEYS.has(key)) {
    return formatDate ? formatDate(raw) : String(raw);
  }

  if (typeof raw === "object") {
    try {
      return JSON.stringify(raw);
    } catch {
      return "—";
    }
  }

  return capitalizeFirstLetter(String(raw));
};

export const mapOutputToDynamicTableRow = (
  output,
  index,
  baseSerialNo,
  listColumns,
  formatDate
) => {
  const statusLower = (output.status || "").toLowerCase();
  const isProcessing = isAutomationOutputProcessing(output.status);

  const row = {
    id: output.id,
    fullData: output,
    isAutomationOutput: true,
    isProcessing,
    isRunAgentPlaceholder: false,
  };

  (listColumns ?? []).forEach((key) => {
    row[key] = formatOutputCellValue(output, key, formatDate);
  });

  return row;
};

export const createRunAgentPlaceholderRow = (listColumns, agentName) => {
  const row = {
    id: "__run_agent_placeholder__",
    isRunAgentPlaceholder: true,
    isProcessing: true,
    fullData: null,
    isAutomationOutput: true,
  };

  (listColumns ?? []).forEach((key) => {
    if (key === "status") {
      row[key] = "Processing";
      return;
    }
    if (key === "source") {
      row[key] = agentName || "—";
      return;
    }
    row[key] = "";
  });

  return row;
};
